import React from 'react'
import { Alert, Stack } from '@mui/material'
import { createPortal } from 'react-dom'
import { SnackbarType } from 'utils/snackbar'
import { useSnackbar } from 'contexts/SnackbarContext'
import { Z_SNACKBAR } from '../constants/layers'

const getAlertSeverity = (type: SnackbarType): 'success' | 'error' | 'warning' | 'info' => {
    switch (type) {
        case SnackbarType.SUCCESS:
            return 'success'
        case SnackbarType.ERROR:
            return 'error'
        case SnackbarType.WARNING:
            return 'warning'
        case SnackbarType.INFO:
            return 'info'
    }
}

const getAlertStyles = (type: SnackbarType) => {
    const baseStyles = {
        success: {
            backgroundColor: '#10b981',
            color: '#ffffff',
            '& .MuiAlert-icon': {
                color: '#ffffff',
            },
        },
        error: {
            backgroundColor: '#ef4444',
            color: '#ffffff',
            '& .MuiAlert-icon': {
                color: '#ffffff',
            },
        },
        warning: {
            backgroundColor: '#f59e0b',
            color: '#ffffff',
            '& .MuiAlert-icon': {
                color: '#ffffff',
            },
        },
        info: {
            backgroundColor: '#3b82f6',
            color: '#ffffff',
            '& .MuiAlert-icon': {
                color: '#ffffff',
            },
        },
    }
    return baseStyles[type] || baseStyles.info
}

export const SnackbarContainer: React.FC = () => {
    const { messages, removeSnackbar } = useSnackbar()

    return createPortal(
        <Stack
            data-testid="snackbar-container"
            spacing={1.5}
            sx={{
                position: 'fixed',
                // ABOVE the modal layer. At the equal z-index these used to share, the modal won on DOM order
                // — it portals to `document.body` and is appended last — so an error raised by a form inside a
                // modal was hidden behind it until the operator closed the very form the error was about.
                // The two values are kept in step by `constants/layers` and its test.
                zIndex: Z_SNACKBAR,
                width: 'auto',
                minWidth: 320,
                maxWidth: 520,
                pointerEvents: 'none',
                top: 24,
                left: '50%',
                transform: 'translateX(-50%)',
            }}
        >
            {messages.map((message) => (
                <Alert
                    key={message.id}
                    onClose={() => removeSnackbar(message.id)}
                    severity={getAlertSeverity(message.type)}
                    sx={{
                        width: '100%',
                        pointerEvents: 'auto',
                        ...getAlertStyles(message.type),
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                        borderRadius: 1.5,
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        letterSpacing: '0.3px',
                        '& .MuiAlert-message': {
                            padding: '8px 0',
                            fontFamily: '"Inter", "-apple-system", "BlinkMacSystemFont", "Segoe UI", sans-serif',
                        },
                        '& .MuiAlert-action': {
                            paddingLeft: 2,
                        },
                    }}
                    action={
                        message.action || message.secondaryAction ? (
                            <div style={{ display: 'flex', gap: 8 }}>
                                {message.secondaryAction && (
                                    <button
                                        onClick={() => {
                                            message.secondaryAction?.onClick()
                                            removeSnackbar(message.id)
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'inherit',
                                            cursor: 'pointer',
                                            padding: '6px 12px',
                                            fontSize: '0.85rem',
                                            fontWeight: 600,
                                            textDecoration: 'none',
                                            opacity: 0.9,
                                            transition: 'opacity 0.2s ease',
                                            borderRadius: '4px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.opacity = '1'
                                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.opacity = '0.9'
                                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                                        }}
                                    >
                                        {message.secondaryAction.title}
                                    </button>
                                )}
                                {message.action && (
                                    <button
                                        onClick={() => {
                                            message.action?.onClick()
                                            removeSnackbar(message.id)
                                        }}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: 'inherit',
                                            cursor: 'pointer',
                                            padding: '6px 12px',
                                            fontSize: '0.85rem',
                                            fontWeight: 600,
                                            textDecoration: 'none',
                                            opacity: 0.9,
                                            transition: 'opacity 0.2s ease',
                                            borderRadius: '4px',
                                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.opacity = '1'
                                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.3)'
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.opacity = '0.9'
                                            e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.2)'
                                        }}
                                    >
                                        {message.action.title}
                                    </button>
                                )}
                            </div>
                        ) : undefined
                    }
                    iconMapping={{
                        success: undefined,
                        error: undefined,
                        warning: undefined,
                        info: undefined,
                    }}
                >
                    {message.message}
                </Alert>
            ))}
        </Stack>,
        document.body
    )
}
